import { useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { ArrowLeft, Mail, Phone, Trash2 } from 'lucide-react';

import { queryKeys } from '@/lib/api/queryKeys';
import { contactsApi } from './api';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';

/**
 * Caso 01 — Contact detail with interaction timeline. The list-page row
 * navigates here; here we expose:
 *   - identity + demographics summary
 *   - editable consent + tags
 *   - timeline of interactions (event / station / date / prize)
 *   - GDPR-style scrub delete.
 */
export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const contactId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const t = useT();
  useFavoritable(t('contacts.detail.title'));

  const [tagsInput, setTagsInput] = useState('');
  /*
   * Sin `window.confirm`: ver `ConfirmDialog`. El estado vive aquí porque el
   * diálogo es controlado y el ciclo de la mutación es de esta pantalla.
   *
   * Y vive ARRIBA, con el resto de hooks: más abajo hay un `return` temprano
   * mientras carga, y un `useState` detrás de él se declara en un render y no
   * en el otro — React cuenta los hooks por orden y revienta el render en
   * cuanto llegan los datos.
   */
  const [borrando, setBorrando] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.contacts.detail(contactId),
    queryFn: () => contactsApi.detail(contactId),
    enabled: Number.isFinite(contactId) && contactId > 0,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Parameters<typeof contactsApi.update>[1]) =>
      contactsApi.update(contactId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.contacts.detail(contactId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => contactsApi.remove(contactId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });
      navigate('/contacts');
    },
  });

  const fullName = useMemo(() => {
    if (!data) return '';
    return (
      [data.firstName, data.lastName].filter(Boolean).join(' ').trim() ||
      t('contacts.anonymousName')
    );
  }, [data, t]);

  if (!Number.isFinite(contactId) || contactId <= 0) {
    return (
      <div className="page">
        <p>{t('contacts.detail.invalidId')}</p>
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="page space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  const demo = data.demographics ?? {};

  function addTag() {
    const tag = tagsInput.trim();
    if (!tag) return;
    const next = [...(data?.tags ?? []), tag];
    updateMutation.mutate({ tags: Array.from(new Set(next)) });
    setTagsInput('');
  }

  function removeTag(tag: string) {
    const next = (data?.tags ?? []).filter((t) => t !== tag);
    updateMutation.mutate({ tags: next });
  }

  function toggleConsent() {
    updateMutation.mutate({ marketingConsent: !data?.marketingConsent });
  }

  return (
    <div className="page space-y-6">
      <button
        type="button"
        onClick={() => navigate('/contacts')}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)]"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('contacts.detail.back')}
      </button>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">{fullName}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-[var(--color-fg-muted)]">
            {data.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" />
                {data.email}
              </span>
            )}
            {data.phone && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" />
                {data.phone}
              </span>
            )}
            {data.marketingConsent ? (
              <Badge tone="success">{t('contacts.consent.yes')}</Badge>
            ) : (
              <Badge tone="neutral">{t('contacts.consent.no')}</Badge>
            )}
            {data.smsConsent ? (
              <Badge tone="info" title={data.smsPhoneE164 ?? undefined}>
                {t('contacts.consent.sms')}
              </Badge>
            ) : null}
          </div>
        </div>
        <Button
          variant="secondary"
          onClick={() => setBorrando(true)}
          disabled={removeMutation.isPending}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {t('contacts.detail.delete')}
        </Button>
      </header>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>{t('contacts.detail.engagement.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Stat
              label={t('contacts.detail.engagement.interactions')}
              value={data.interactionsCount}
            />
            <Stat
              label={t('contacts.detail.engagement.prizes')}
              value={data.prizesWonCount}
            />
            <Stat
              label={t('contacts.detail.engagement.firstSeen')}
              value={
                data.firstSeenAt
                  ? dayjs(data.firstSeenAt).format('MMM D, YYYY')
                  : '—'
              }
            />
            <Stat
              label={t('contacts.detail.engagement.lastSeen')}
              value={
                data.lastSeenAt
                  ? dayjs(data.lastSeenAt).format('MMM D, YYYY')
                  : '—'
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('contacts.detail.demographics.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <DemoRow label={t('contacts.detail.demographics.age')} value={demo.ageRange} />
            <DemoRow label={t('contacts.detail.demographics.gender')} value={demo.gender} />
            <DemoRow label={t('contacts.detail.demographics.city')} value={demo.city} />
            <DemoRow label={t('contacts.detail.demographics.state')} value={demo.state} />
            <DemoRow label={t('contacts.detail.demographics.zip')} value={demo.zip} />
            <DemoRow label={t('contacts.detail.demographics.language')} value={demo.language} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('contacts.detail.controls.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant={data.marketingConsent ? 'secondary' : 'primary'}
              onClick={toggleConsent}
              disabled={updateMutation.isPending}
              className="w-full"
            >
              {data.marketingConsent
                ? t('contacts.detail.controls.revokeConsent')
                : t('contacts.detail.controls.grantConsent')}
            </Button>

            <div>
              <label className="text-xs font-semibold uppercase text-[var(--color-fg-muted)]">
                {t('contacts.detail.controls.tags')}
              </label>
              <div className="mt-1 flex flex-wrap gap-1">
                {(data.tags ?? []).map((tag) => (
                  <button
                    type="button"
                    key={tag}
                    onClick={() => removeTag(tag)}
                    className="rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-xs"
                    title={t('contacts.detail.controls.removeTag')}
                  >
                    {tag} ✕
                  </button>
                ))}
              </div>
              <div className="mt-2 flex gap-1">
                <input
                  type="text"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTag()}
                  className="flex-1 rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-2 py-1 text-sm"
                  placeholder={t('contacts.detail.controls.addTagPlaceholder')}
                />
                <Button variant="secondary" onClick={addTag}>
                  {t('contacts.detail.controls.add')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {t('contacts.detail.timeline.title', {
              count: data.interactions.length,
            })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.interactions.length === 0 ? (
            <p className="text-sm text-[var(--color-fg-muted)]">
              {t('contacts.detail.timeline.empty')}
            </p>
          ) : (
            <ol className="space-y-3">
              {data.interactions.map((i) => (
                <li
                  key={i.id}
                  className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--color-border-subtle)] pb-2"
                >
                  <div>
                    <div className="text-sm font-medium">
                      {i.xkEventId
                        ? t('contacts.detail.timeline.atEvent', {
                            id: i.xkEventId,
                          })
                        : t('contacts.detail.timeline.unknownEvent')}
                    </div>
                    <div className="text-xs text-[var(--color-fg-muted)]">
                      {i.xkStationId
                        ? t('contacts.detail.timeline.station', {
                            id: i.xkStationId,
                          })
                        : ''}
                      {i.source ? ` · ${i.source}` : ''}
                      {i.prizeStockId
                        ? ` · ${t('contacts.detail.timeline.prize', {
                            id: i.prizeStockId,
                          })}`
                        : ''}
                    </div>
                  </div>
                  <div className="text-xs text-[var(--color-fg-muted)] tabular-nums">
                    {i.submittedAt
                      ? dayjs(i.submittedAt).format('MMM D, YYYY HH:mm')
                      : '—'}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={borrando}
        onOpenChange={setBorrando}
        tone="danger"
        title={t('contacts.detail.delete')}
        description={t('contacts.detail.deleteConfirm', { name: fullName })}
        confirmLabel={t('contacts.detail.delete')}
        loading={removeMutation.isPending}
        onConfirm={() => removeMutation.mutate()}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-baseline justify-between py-1.5">
      <span className="text-xs uppercase text-[var(--color-fg-muted)]">
        {label}
      </span>
      <span className="text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function DemoRow({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) {
  return (
    <div className="flex items-baseline justify-between py-1.5">
      <span className="text-xs uppercase text-[var(--color-fg-muted)]">
        {label}
      </span>
      <span className="text-sm">{value || '—'}</span>
    </div>
  );
}
